import type { IDBPDatabase, IDBPTransaction, StoreNames } from 'idb';
import type { SagsDb } from '../db';

/**
 * Explicit, numbered migrations from v1 — the pattern exists from day one so that
 * the first real schema change is a routine act rather than an improvised one. The
 * data in this database is a person's vocabulary and their own recorded phrasing;
 * it accumulates value from the day Phase 2 ships and cannot be regenerated.
 *
 * Rules:
 *  - never edit a shipped migration, add the next one
 *  - migrations run inside the `versionchange` transaction, so they must use `tx`
 *    and must not await anything outside it
 *  - `SCHEMA_VERSION` is derived, never hand-maintained
 */

export type UpgradeTx = IDBPTransaction<SagsDb, StoreNames<SagsDb>[], 'versionchange'>;

export interface MigrationContext {
  db: IDBPDatabase<SagsDb>;
  tx: UpgradeTx;
  oldVersion: number;
  newVersion: number;
}

export interface Migration {
  version: number;
  description: string;
  /**
   * May be async.
   *
   * v1 only created stores, which is synchronous. v2 is the first migration that has to **read and
   * write rows**, and awaiting a request inside the `versionchange` transaction is the only correct way
   * to do that — floating the promise would let the transaction complete before the write landed, which
   * would fail silently and leave the device half-migrated.
   */
  up: (ctx: MigrationContext) => void | Promise<void>;
}

/**
 * Stores that no longer exist in the current schema, but did when a past migration ran.
 *
 * `SagsDb` describes the schema **as it is now**; a migration describes **how it got here**. v1
 * creates `lexicon` and `bigrams` and v2 removes them, so both legitimately name stores the live type
 * cannot contain — and the rule at the top of this file is that a shipped migration is never edited,
 * because a device that has already applied it must replay the same history.
 *
 * These two helpers are where that tension is expressed once, with a cast, rather than scattered
 * through the migrations as ad-hoc suppressions.
 */
const asNative = (db: IDBPDatabase<SagsDb>): IDBDatabase => db as unknown as IDBDatabase;

function createLegacyStore(
  db: IDBPDatabase<SagsDb>,
  name: string,
  options?: IDBObjectStoreParameters,
): void {
  asNative(db).createObjectStore(name, options);
}

function dropStore(db: IDBPDatabase<SagsDb>, name: string): boolean {
  const native = asNative(db);
  if (!native.objectStoreNames.contains(name)) return false;
  native.deleteObjectStore(name);
  return true;
}

/** v1 — initial schema (design §6). */
const v1: Migration = {
  version: 1,
  description: 'initial schema',
  up: ({ db }) => {
    // Singletons: one row, fixed key, no keyPath.
    db.createObjectStore('settings');
    db.createObjectStore('meta');

    const quickFires = db.createObjectStore('quickFires', { keyPath: 'id' });
    quickFires.createIndex('byOrder', 'order');

    const categories = db.createObjectStore('categories', { keyPath: 'id' });
    categories.createIndex('byOrder', 'order');

    const phrases = db.createObjectStore('phrases', { keyPath: 'id' });
    phrases.createIndex('byCategory', 'categoryId');
    phrases.createIndex('byOrder', 'order');

    db.createObjectStore('abbrevs', { keyPath: 'code' });

    // Seeded read-only prediction data, per design §6. Phase 2 established that the lexicon cannot
    // live here — 50 000 rows cannot build a trie inside 200 ms — so **v2 drops both**. They are still
    // created here because a shipped migration is never edited: a device that applied v1 has these
    // stores, and its history must replay identically.
    createLegacyStore(db, 'lexicon', { keyPath: 'word' });
    createLegacyStore(db, 'bigrams', { keyPath: ['prev', 'next'] });

    // Learned. Personal counts outrank corpus counts in Phase 2's scoring, which is
    // exactly why they are the part of this database that must survive.
    db.createObjectStore('personalUnigrams', { keyPath: 'word' });
    db.createObjectStore('personalBigrams', { keyPath: ['prev', 'next'] });
    db.createObjectStore('candidatePhrases', { keyPath: 'text' });

    const history = db.createObjectStore('history', { keyPath: 'id' });
    history.createIndex('bySpokenAt', 'spokenAt');

    const calibration = db.createObjectStore('calibration', { keyPath: 'id' });
    calibration.createIndex('byRanAt', 'ranAt');

    const diagnostics = db.createObjectStore('diagnostics', { keyPath: 'id' });
    diagnostics.createIndex('byTs', 'ts');
    diagnostics.createIndex('byType', 'type');
  },
};

/**
 * v2 — drop the corpus stores, and fix prediction weights that were never used.
 *
 * **The stores.** Design §6 lists `lexicon` and `bigrams` as IndexedDB stores, while §5.3 requires a
 * packed binary parsed in a worker with a trie ready inside 200 ms. Reading 50 000 rows out of
 * IndexedDB on every launch cannot meet that, so the packed artefact at `public/lexicon/de-1.bin` is
 * the real design and these two stores were only ever going to mislead whoever read the schema next
 * (C40). Nothing has ever written to them and neither appears in `SagsExport`, so nothing is lost.
 *
 * **The weights.** `predictionWeights` shipped as `[1, 3, 1, 3, 1, 1]`, and with normalised scoring the
 * sixth term wants 0.5 — at 1 a 25-character compound with a corpus frequency of 20 ties with `wir` at
 * 30 000 (C42). Prediction has never been enabled, so these numbers have never affected anything the
 * person saw; correcting them here is cheaper than leaving a known-bad default on the device for a
 * caregiver to discover.
 *
 * A settings row written by a caregiver keeps every other field: only the weights are replaced, and
 * only if they are still the old default. Someone who has deliberately tuned them is left alone.
 */
const v2: Migration = {
  version: 2,
  description: 'drop unused corpus stores; correct untouched prediction weights',
  up: async ({ db, tx }) => {
    for (const name of ['lexicon', 'bigrams']) dropStore(db, name);

    // Awaited, and using `tx` rather than opening its own: the versionchange transaction stays alive
    // while its requests are pending, and a floated promise would race the transaction closing.
    const settings = tx.objectStore('settings');
    const stored = await settings.get('settings');
    if (!stored) return;

    const OLD_DEFAULT = [1, 3, 1, 3, 1, 1];
    const current = stored.predictionWeights;
    const untouched =
      Array.isArray(current) &&
      current.length === OLD_DEFAULT.length &&
      current.every((value, index) => value === OLD_DEFAULT[index]);

    // Someone who has deliberately tuned the weights is left alone.
    if (!untouched) return;
    await settings.put({ ...stored, predictionWeights: [1, 3, 1, 3, 1, 0.5] }, 'settings');
  },
};

/**
 * v3 — turn prediction on for the device in service.
 *
 * Changing `DEFAULT_SETTINGS.predictionEnabled` reaches only *fresh installs*: this person's settings
 * row already holds `false`, written when Phase 1 seeded it. Defaults describe a new database; a
 * database in service needs a migration. Both are needed and only together do they mean "on".
 *
 * Unconditional, and safe *because of when it runs*: prediction has never been enabled, so there is no
 * caregiver preference to override. Once this has run, that stops being true — **no future migration may
 * set this flag again.** If a caregiver turns it off, that is a decision, and stage 3's A/B is
 * explicitly allowed to conclude that off is right for this person.
 *
 * It also cannot arrive unattended. A new service worker only activates on an explicit
 * "Update installieren" tap in Betreuer-Modus (non-negotiable §3.2), so somebody is present at the
 * moment four new words appear above a keyboard this person has spent weeks learning.
 */
const v3: Migration = {
  version: 3,
  description: 'enable prediction on a device that predates it',
  up: async ({ tx }) => {
    const settings = tx.objectStore('settings');
    const stored = await settings.get('settings');
    if (!stored) return;
    if (stored.predictionEnabled) return;
    await settings.put({ ...stored, predictionEnabled: true }, 'settings');
  },
};

export const MIGRATIONS: readonly Migration[] = [v1, v2, v3];

export const SCHEMA_VERSION = MIGRATIONS.reduce((max, m) => Math.max(max, m.version), 0);

/**
 * Apply every migration in `(oldVersion, newVersion]`, in ascending order.
 *
 * Exported separately from `getDb` so it can be unit-tested against a synthetic
 * migration list — that test is what proves the *pattern* works before we depend
 * on it for a real schema change.
 */
export async function runMigrations(
  migrations: readonly Migration[],
  ctx: MigrationContext,
): Promise<number[]> {
  const applied: number[] = [];
  const ordered = [...migrations].sort((a, b) => a.version - b.version);

  for (const migration of ordered) {
    if (migration.version <= ctx.oldVersion) continue;
    if (migration.version > ctx.newVersion) continue;
    // Sequential and awaited: v3 must not begin before v2's writes have landed.
    await migration.up(ctx);
    applied.push(migration.version);
  }

  return applied;
}
